import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useOptionalDependency } from 'ditox-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TRANSCODER_TOKEN } from '@/container';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import { ResponseError, responseScheme } from '@/models/response';
import { convertFileToUploadable, convertUploadableToFile, type Uploadable } from '@/models/uploadable';
import { promiseLogger, SENTRY_LOG } from '@/services/sentry';
import { addToast, addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { readableFileSize } from '@/utils/file-size';

export default function useUploadFile(adminChatSessionId?: number) {
  const { t } = useTranslation();
  const { getFileSizeLimit, isLoading: isSystemSettingLoading } = useFileSizeLimit(!!adminChatSessionId);
  const transcoder = useOptionalDependency(TRANSCODER_TOKEN);

  const mutation = useMutation<UploadFileResponseType, ResponseError, Uploadable>({
    mutationKey: ['/UploadFile'],
    mutationFn: async (params) => {
      let uploadable: Uploadable = params;

      if (params.compress && transcoder) {
        try {
          let output = await convertUploadableToFile(params);
          const fileType = params.type.split('/')[0];

          switch (fileType) {
            case 'video':
              output = await transcoder.transcodeVideo(output);
              break;
            case 'image':
              output = await transcoder.transcodeImage(output);
              break;
            case 'audio':
              output = await transcoder.transcodeAudio(output);
              break;
          }
          uploadable = convertFileToUploadable(output);
          uploadable.compress = false;
        } catch (e) {
          console.error(e);
        }
      }

      return promiseLogger({
        name: SENTRY_LOG.UPLOAD,
        action: async () =>
          fetcher(
            '/UploadFile',
            { file: await convertUploadableToFile(uploadable) },
            {
              schema: uploadFileResponseScheme,
              requestContentType: 'form-data',
              validResponseCodes: [0],
              formDataPayload: { ChatSessionID: adminChatSessionId },
            },
          ),
        requestAttributes: {
          'file-size': `${uploadable.size}`,
          'file-type': uploadable.type,
        },
      });
    },
    retry: false,
    onError: (error) => {
      if (ResponseError.isResponseError(error)) {
        addToast({
          type: 'error',
          message: error.message,
        });
      } else {
        addTranslateToast({
          type: 'error',
          message: 'upload.failed',
        });
      }
    },
  });

  const upload = async (uploadable: Uploadable) => {
    const fileSizeLimit = getFileSizeLimit(uploadable.type.split('/')[0]);
    if (uploadable.size >= fileSizeLimit) {
      addToast({
        type: 'error',
        message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
      });

      throw 'FILE_TOO_LARGE';
    }

    mutation.reset();

    const response = await mutation.mutateAsync(uploadable);

    if (response.Code !== 0) {
      addTranslateToast({
        type: 'error',
        message: 'upload.failed',
      });
      throw new ResponseError(response.Code, response.Msg);
    }

    return response;
  };

  // 4. return callback
  return {
    upload,
    isLoading: isSystemSettingLoading || mutation.isPending,
    ...mutation,
  };
}

export function useIsUploadingFile() {
  const uploadingCount = useIsMutating({ mutationKey: ['/UploadFile'] });

  return uploadingCount > 0;
}

const uploadFileResponseScheme = responseScheme.extend({
  FileUrl: z.string().url(),
});

type UploadFileResponseType = z.infer<typeof uploadFileResponseScheme>;
